<template>
  <b-modal
    id="basic-test-modal"
    ref="modal"
    :title="modalTitle"
    content-class="basic-test-modal-content"
    header-class="basic-test-modal-head"
    body-class="basic-test-modal-body"
    footer-class="basic-test-modal-foot"
    size="lg"
    @hidden="onHidden"
  >
    <div class="basic-test-body-inner">
      <!-- Loading -->
      <div v-if="loading" class="basic-test-loading">
        <b-spinner type="grow" class="basic-test-spinner" />
        <p class="basic-test-loading-text">Загрузка теста…</p>
      </div>

      <!-- Submitting result (all correct, POST in progress) -->
      <div v-else-if="submittingResult" class="basic-test-loading">
        <b-spinner type="grow" class="basic-test-spinner" />
        <p class="basic-test-loading-text">Отправка результата…</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="basic-test-error">
        <span class="basic-test-error-icon">!</span>
        <p class="mb-0">{{ error }}</p>
      </div>

      <!-- Result: passed -->
      <div v-else-if="step === 'result' && result === 'passed'" class="basic-test-result basic-test-result-passed">
        <div class="basic-test-passed-alert">
          <b-icon icon="check-circle-fill" class="basic-test-passed-alert-icon" />
          <div class="basic-test-passed-alert-body">
            <p class="basic-test-result-text">
              Вы успешно прошли тестирование на понимание рисков, связанных с операциями с цифровыми активами. Вам предоставлен доступ к торговым операциям на платформе.
            </p>
          </div>
        </div>
      </div>

      <!-- Result: failed -->
      <div v-else-if="step === 'result' && result === 'failed'" class="basic-test-result basic-test-result-failed">
        <div class="basic-test-failed-alert">
          <b-icon icon="exclamation-triangle" class="basic-test-failed-alert-icon" />
          <div class="basic-test-failed-alert-body">
            <p class="basic-test-result-text">
              Вы не прошли тестирование на понимание рисков, связанных с операциями с цифровыми активами.
            </p>
            <p class="basic-test-result-subtext">
              Перед повторной попыткой снова откройте «Предупреждение о рисках» и подтвердите ознакомление на следующем шаге.
            </p>
          </div>
        </div>
        <div class="basic-test-failed-actions">
          <button type="button" class="basic-test-retry-btn" @click="retry">
            <b-icon icon="arrow-repeat" class="basic-test-retry-icon" />
            <span>Пройти тест повторно</span>
          </button>
        </div>
      </div>

      <!-- Mandatory risk disclosure before first question or retry -->
      <div v-else-if="step === 'risk' && questions.length" class="basic-test-risk-gate">
        <div class="basic-test-risk-gate-icon-wrap">
          <b-icon icon="exclamation-triangle-fill" class="basic-test-risk-gate-icon" />
        </div>
        <h5 class="basic-test-risk-gate-title">Предупреждение о рисках</h5>
        <p class="basic-test-risk-gate-text">
          Перед прохождением теста необходимо открыть и ознакомиться с документом
          <a :href="riskDocHref" class="basic-test-risk-link" target="_blank" rel="noopener noreferrer">«Предупреждение о рисках»</a>
          (откроется в новой вкладке). Без этого шага доступ к вопросам теста недоступен.
        </p>
        <label class="basic-test-risk-gate-check">
          <input v-model="riskReadConfirmed" type="checkbox" class="basic-test-risk-gate-checkbox" />
          <span>Подтверждаю, что ознакомился(лась) с предупреждением о рисках перед прохождением теста</span>
        </label>
      </div>

      <!-- Questions -->
      <div v-else-if="step === 'questions' && questions.length" class="basic-test-questions">
        <h5 class="basic-test-questions-title">{{ testTitleRu }}</h5>
        <p class="basic-test-progress">Вопрос {{ currentQuestionIndex + 1 }} из {{ questions.length }}</p>
        <div class="basic-test-question-block">
          <p class="basic-test-question-text">{{ (currentQuestion.questionRu || '').trim() }}</p>
          <div class="basic-test-answers">
            <label
              v-for="a in currentQuestion.answers"
              :key="a.answerId"
              class="basic-test-answer-label"
              :class="{ checked: isSelected(currentQuestion.questionId, a.answerId) }"
            >
              <input
                type="radio"
                :name="'q' + currentQuestion.questionId"
                :value="a.answerId"
                :checked="isSelected(currentQuestion.questionId, a.answerId)"
                @change="selectAnswer(currentQuestion.questionId, a)"
              />
              <span class="basic-test-answer-text">{{ (a.answerRu || '').trim() }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <template v-if="loading || submittingResult || error">
        <b-button variant="outline-light" size="sm" class="basic-test-close-btn" @click="$bvModal.hide('basic-test-modal')">Закрыть</b-button>
      </template>
      <template v-else-if="step === 'result' && result === 'passed'">
        <b-button variant="outline-light" size="sm" class="basic-test-close-btn" @click="passedClose">Закрыть</b-button>
      </template>
      <template v-else-if="step === 'result' && result === 'failed'">
        <!-- Buttons are in body -->
        <b-button variant="outline-light" size="sm" class="basic-test-close-btn" @click="$bvModal.hide('basic-test-modal')">Закрыть</b-button>
      </template>
      <template v-else-if="step === 'risk' && questions.length">
        <b-button variant="outline-light" size="sm" class="basic-test-close-btn mr-2" @click="$bvModal.hide('basic-test-modal')">Закрыть</b-button>
        <b-button
          variant="warning"
          size="sm"
          class="basic-test-continue-btn"
          :disabled="!riskReadConfirmed"
          @click="proceedToQuestions"
        >
          Перейти к тесту
        </b-button>
      </template>
      <template v-else-if="step === 'questions' && questions.length">
        <b-button
          v-if="currentQuestionIndex > 0"
          variant="outline-light"
          size="sm"
          class="basic-test-close-btn mr-2"
          @click="prev"
        >Назад</b-button>
        <b-button
          v-if="!isLastQuestion"
          variant="outline-light"
          size="sm"
          class="basic-test-close-btn"
          :disabled="!canGoNext"
          @click="next"
        >Далее</b-button>
        <b-button
          v-else
          variant="outline-light"
          size="sm"
          class="basic-test-close-btn"
          :disabled="!canGoNext"
          @click="submit"
        >Завершить тест</b-button>
      </template>
    </template>
  </b-modal>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '@/config/config'
import { EventBus } from '@/EventBus'

export default {
  name: 'BasicTestModal',

  data() {
    return {
      loading: false,
      error: null,
      testData: null,
      step: 'questions', // 'risk' | 'questions' | 'result'
      riskReadConfirmed: false,
      currentQuestionIndex: 0,
      selectedAnswers: {}, // { questionId: answer }
      result: null, // null | 'passed' | 'failed'
      submittingResult: false
    }
  },

  computed: {
    modalTitle() {
      if (this.step === 'result' && this.result === 'passed') return 'Тест пройден'
      if (this.step === 'result' && this.result === 'failed') return 'Тест не пройден'
      if (this.step === 'risk') return 'Ознакомление с рисками'
      return 'Тест на понимание рисков'
    },
    testTitleRu() {
      return (this.testData && this.testData.testTitleRu) ? this.testData.testTitleRu : 'Тест на понимание рисков'
    },
    questions() {
      if (!this.testData || !Array.isArray(this.testData.questions)) return []
      return this.testData.questions
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || null
    },
    isLastQuestion() {
      return this.questions.length > 0 && this.currentQuestionIndex === this.questions.length - 1
    },
    canGoNext() {
      if (!this.currentQuestion) return false
      return this.selectedAnswers[this.currentQuestion.questionId] != null
    },
    riskDocHref() {
      const base = window.location.href.split('#')[0]
      return base + '#/risk'
    }
  },

  methods: {
    show() {
      this.loading = true
      this.error = null
      this.testData = null
      this.riskReadConfirmed = false
      this.step = 'questions'
      this.currentQuestionIndex = 0
      this.selectedAnswers = {}
      this.result = null
      this.$bvModal.show('basic-test-modal')
      this.loadTest()
    },

    shuffleArray(arr) {
      const a = arr.slice()
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]]
      }
      return a
    },
    loadTest() {
      const url = `${apiDomain}/ptest/basic/BASIC`
      axios
        .get(url, { headers: getHeader() })
        .then(res => {
          const raw = res.data
          const data = (raw && Array.isArray(raw.questions))
            ? { ...raw, questions: this.shuffleArray(raw.questions).map(q => ({ ...q, answers: this.shuffleArray(q.answers || []) })) }
            : raw
          this.testData = data
          this.error = null
          this.riskReadConfirmed = false
          this.step = 'risk'
          this.currentQuestionIndex = 0
          this.selectedAnswers = {}
        })
        .catch(err => {
          this.error = (err.response && err.response.data && err.response.data.message) || err.message || 'Не удалось загрузить тест. Попробуйте позже.'
          this.testData = null
        })
        .finally(() => {
          this.loading = false
        })
    },

    selectAnswer(questionId, answer) {
      this.$set(this.selectedAnswers, questionId, answer)
    },

    isSelected(questionId, answerId) {
      const a = this.selectedAnswers[questionId]
      return a && a.answerId === answerId
    },

    next() {
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++
      }
    },

    prev() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--
      }
    },

    submit() {
      const allCorrect = this.questions.every(q => {
        const chosen = this.selectedAnswers[q.questionId]
        return chosen && chosen.isCorrect === 1
      })
      if (!allCorrect) {
        this.result = 'failed'
        this.step = 'result'
        return
      }
      const correctAnswerCount = this.questions.filter(
        q => this.selectedAnswers[q.questionId] && this.selectedAnswers[q.questionId].isCorrect === 1
      ).length
      const totalQuestionCount = this.questions.length
      const testCode = 'BASIC'
      this.submittingResult = true
      this.error = null
      const url = `${apiDomain}/ptest/postbasic`
      // Per-question payload so the backend can persist the run into
      // p_test_answer_response — without this the admin "Тестирование"
      // tab has nothing to render for BASIC. Matches the shape the
      // qualified-test modal sends.
      const answers = this.questions.map(q => {
        const chosen = this.selectedAnswers[q.questionId] || {}
        return {
          questionId: q.questionId,
          answerId:   chosen.answerId,
          isCorrect:  chosen.isCorrect
        }
      })
      axios
        .post(url, { correctAnswerCount, totalQuestionCount, testCode, answers }, { headers: getHeader() })
        .then(res => {
          const ok = res.status === 200 && (
            res.data === 'success' ||
            res.data === 'SUCCESS' ||
            (res.data && (res.data.status === 'success' || res.data.success === true))
          )
          if (ok) {
            this.submittingResult = false
            this.result = 'passed'
            this.step = 'result'
            this.$emit('passed', { testId: this.testData.testId, answers: this.selectedAnswers })
            EventBus.$emit('refresh-user-details')
          } else {
            this.submittingResult = false
            this.error = (res.data && (res.data.message || res.data.msg)) || 'Не удалось сохранить результат теста.'
          }
        })
        .catch(err => {
          this.submittingResult = false
          const msg = err.response && err.response.data && (err.response.data.message || err.response.data.msg)
          this.error = msg || err.message || 'Не удалось сохранить результат теста. Попробуйте позже.'
        })
    },

    passedClose() {
      this.$emit('passed-close')
      this.$bvModal.hide('basic-test-modal')
    },

    proceedToQuestions() {
      if (!this.riskReadConfirmed) return
      this.step = 'questions'
    },

    retry() {
      this.loading = true
      this.error = null
      this.testData = null
      this.riskReadConfirmed = false
      this.step = 'questions'
      this.currentQuestionIndex = 0
      this.selectedAnswers = {}
      this.result = null
      this.loadTest()
    },

    onHidden() {
      this.error = null
      this.testData = null
      this.riskReadConfirmed = false
      this.step = 'questions'
      this.currentQuestionIndex = 0
      this.selectedAnswers = {}
      this.result = null
      this.submittingResult = false
    }
  }
}
</script>

<style>
/* Basic test modal */
#basic-test-modal.modal,
#basic-test-modal .modal-dialog {
  align-items: flex-start !important;
  justify-content: center;
}
#basic-test-modal.modal {
  padding-top: 2rem;
  padding-bottom: 2rem;
}
#basic-test-modal .modal-content.basic-test-modal-content {
  background: #252840;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}
#basic-test-modal .modal-header.basic-test-modal-head {
  background: #252840;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: 800;
  padding: 20px 28px;
  letter-spacing: -0.02em;
}
#basic-test-modal .modal-header.basic-test-modal-head .modal-title {
  color: #fff;
  font-weight: 800;
  font-size: 1.25rem;
}
#basic-test-modal .modal-header .close {
  color: rgba(255, 255, 255, 0.7);
  text-shadow: none;
  opacity: 1;
  font-size: 1.5rem;
}
#basic-test-modal .modal-header .close:hover {
  color: #fff;
}
#basic-test-modal .modal-body.basic-test-modal-body {
  background: #252840;
  color: rgba(255, 255, 255, 0.95);
  padding: 28px;
  min-height: 220px;
  border: none;
}
#basic-test-modal .basic-test-body-inner {
  min-height: 180px;
  color: rgba(255, 255, 255, 0.95);
}
#basic-test-modal .modal-footer.basic-test-modal-foot {
  background: #252840;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 16px 28px;
}
#basic-test-modal .basic-test-close-btn {
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 12px;
  transition: all 0.2s ease;
}
#basic-test-modal .basic-test-close-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.95);
  color: #252840;
  border-color: rgba(255, 255, 255, 0.95);
}
#basic-test-modal .basic-test-close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
#basic-test-modal .basic-test-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 28px;
  min-height: 160px;
}
#basic-test-modal .basic-test-spinner {
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
}
#basic-test-modal .basic-test-loading-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-weight: 600;
  margin: 0;
}
#basic-test-modal .basic-test-error {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.95);
}
#basic-test-modal .basic-test-error p {
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}
#basic-test-modal .basic-test-error-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.9);
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
}
/* Result screens */
#basic-test-modal .basic-test-result {
  padding: 4px 0;
}
#basic-test-modal .basic-test-result-text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 15px;
  line-height: 1.55;
  margin: 0 0 8px 0;
}
#basic-test-modal .basic-test-result-subtext {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}
/* Passed result: success alert */
#basic-test-modal .basic-test-passed-alert {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 14px;
  background: rgba(25, 135, 84, 0.15);
  border: 1px solid rgba(25, 135, 84, 0.4);
}
#basic-test-modal .basic-test-passed-alert-icon {
  flex-shrink: 0;
  font-size: 1.5rem;
  color: rgba(25, 135, 84, 0.95);
  margin-top: 2px;
}
#basic-test-modal .basic-test-passed-alert-body {
  flex: 1;
  min-width: 0;
}
#basic-test-modal .basic-test-passed-alert .basic-test-result-text {
  margin: 0;
}
/* Failed result: alert-style message */
#basic-test-modal .basic-test-failed-alert {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 20px;
  margin-bottom: 24px;
  border-radius: 14px;
  background: rgba(220, 53, 69, 0.12);
  border: 1px solid rgba(220, 53, 69, 0.35);
}
#basic-test-modal .basic-test-failed-alert-icon {
  flex-shrink: 0;
  font-size: 1.5rem;
  color: rgba(220, 53, 69, 0.95);
  margin-top: 2px;
}
#basic-test-modal .basic-test-failed-alert-body {
  flex: 1;
  min-width: 0;
}
#basic-test-modal .basic-test-failed-alert .basic-test-result-text {
  margin-bottom: 6px;
}
#basic-test-modal .basic-test-failed-alert .basic-test-result-subtext {
  margin: 0;
}
/* Failed result: actions */
#basic-test-modal .basic-test-failed-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
#basic-test-modal .basic-test-failed-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
}
#basic-test-modal .basic-test-risk-link {
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.92);
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  padding-bottom: 2px;
  transition: color 0.2s ease, border-color 0.2s ease;
}
#basic-test-modal .basic-test-risk-link:hover {
  color: #fff;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}
/* Risk gate (mandatory read before questions) */
#basic-test-modal .basic-test-risk-gate {
  padding: 8px 4px 12px;
  text-align: left;
}
#basic-test-modal .basic-test-risk-gate-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(239, 185, 20, 0.12);
  border: 1px solid rgba(239, 185, 20, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
#basic-test-modal .basic-test-risk-gate-icon {
  font-size: 1.5rem;
  color: rgba(239, 185, 20, 0.95);
}
#basic-test-modal .basic-test-risk-gate-title {
  color: rgba(255, 255, 255, 0.96);
  font-size: 1.1rem;
  font-weight: 800;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
}
#basic-test-modal .basic-test-risk-gate-text {
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
  line-height: 1.55;
  margin: 0 0 18px 0;
}
#basic-test-modal .basic-test-risk-gate-text .basic-test-risk-link {
  font-size: inherit;
}
#basic-test-modal .basic-test-risk-gate-check {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  margin: 0;
}
#basic-test-modal .basic-test-risk-gate-checkbox {
  margin-top: 3px;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  cursor: pointer;
}
#basic-test-modal .basic-test-continue-btn {
  font-weight: 800;
  border-radius: 10px;
}
#basic-test-modal .basic-test-continue-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
/* Retry button with icon */
#basic-test-modal .basic-test-retry-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 24px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-end;
}
#basic-test-modal .basic-test-retry-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.6);
  color: #fff;
}
#basic-test-modal .basic-test-retry-icon {
  font-size: 1.1rem;
}
/* Questions */
#basic-test-modal .basic-test-questions-title {
  color: #fff;
  font-weight: 800;
  font-size: 1.1rem;
  margin-bottom: 8px;
}
#basic-test-modal .basic-test-progress {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  margin-bottom: 18px;
}
#basic-test-modal .basic-test-question-block {
  margin-top: 8px;
}
#basic-test-modal .basic-test-question-text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 14px;
  line-height: 1.4;
}
#basic-test-modal .basic-test-answers {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
#basic-test-modal .basic-test-answer-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
#basic-test-modal .basic-test-answer-label:hover,
#basic-test-modal .basic-test-answer-label.checked {
  border-color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.06);
}
#basic-test-modal .basic-test-answer-label input {
  margin-top: 3px;
  flex-shrink: 0;
}
#basic-test-modal .basic-test-answer-text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  line-height: 1.4;
}
</style>
